import { useCallback } from 'react';
import type { WordCloudStaticContent } from '@core-api/presentation/types/response';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { getVisualizationState } from '../../utils/getVisualizationState';
import { useScreenshotReady } from '../../shared/hooks/use-screenshot-ready';
import { EditorEmptyState } from './presentation/EditorEmptyState';
import { WordCloudPresentation } from './WordCloudPresentation';
import { useWordCloudPreviewResults } from './hooks/use-wordcloud-preview-results';
import { useTextResults } from './hooks/use-text-results';

export function WordCloudPresentationDataHandler() {
  const {
    InteractiveCanvasComponents,
    useConfig,
    useModeration,
    isPreviewResultsEnabled,
    usePace,
    useCurrentSession,
  } = usePresentationContext();
  const slide = usePresentationSlideContext();
  const { isCurrentSessionActive, activeSessionId } = useCurrentSession();
  const staticContent = slide.staticContent as WordCloudStaticContent;
  const interactiveContent = slide.interactiveContents[0]!;

  const { readonly, enableAnimations, mode } = useConfig();
  const { currentStep } = usePace();

  // Moderation
  const { showUi, toggleWord } = useModeration();
  const handleRemoveWord = useCallback(
    (key: string) => (showUi ? toggleWord(key) : undefined),
    [showUi, toggleWord],
  );

  const visualizationState = getVisualizationState({
    mode,
    currentStep,
  });

  const shouldHideResults =
    slide.responseVisibility === 'hidden' ||
    visualizationState === 'hide-answers';

  const { results, isLoading } = useTextResults({
    adminKey: slide.slideAdminKey,
    interactiveContentId: interactiveContent.interactiveContentId,
    shouldFetchResults: !isPreviewResultsEnabled,
    sessionId: isCurrentSessionActive ? undefined : activeSessionId,
  });

  const previewResults = useWordCloudPreviewResults({
    isEnabled: isPreviewResultsEnabled,
  });

  useScreenshotReady({ isReady: mode === 'screenshot' && !isLoading });

  if (mode === 'idle' && results.length === 0 && !isPreviewResultsEnabled) {
    return (
      <EditorEmptyState
        title={slide.title}
        styledTitle={staticContent.styledTitle as Record<string, unknown>}
        label={staticContent.label}
        longerDescription={interactiveContent.description}
        fontSizeOffset={staticContent.textSizeOffset}
        CanvasVisualizationArea={
          InteractiveCanvasComponents?.CanvasVisualizationArea
        }
        textAccessibleColors={slide.design?.colors?.textAccessibleColors}
      />
    );
  }

  return (
    <WordCloudPresentation
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      longerDescription={interactiveContent.description}
      fontSizeOffset={staticContent.textSizeOffset}
      shouldHideResults={shouldHideResults}
      onRemoveWord={handleRemoveWord}
      isModerationEnabled={!readonly && showUi}
      results={isPreviewResultsEnabled ? previewResults : results}
      shouldAnimate={enableAnimations}
      CanvasVisualizationArea={
        InteractiveCanvasComponents?.CanvasVisualizationArea
      }
      isPreviewResultsEnabled={isPreviewResultsEnabled}
      textAccessibleColors={slide.design?.colors?.textAccessibleColors}
    />
  );
}
